import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import Hero from "../components/Hero";
import Ticker from "../components/Ticker";
import Framework from "../components/Framework";
import MyProtocol from "../components/MyProtocol";
import MyBloodwork from "../components/MyBloodwork";
import Testimator from "../components/Testimator";
import Reviews from "../components/Reviews";
import KnowledgeBase from "../components/KnowledgeBase";
import TheBuild from "../components/TheBuild";
import FinalCTA from "../components/FinalCTA";
import Footer from "../components/Footer";
import useJsonLd from "../lib/useJsonLd";
import { REVIEWS_ALL, REVIEWS_AGGREGATE, APP_STORE } from "../data/reviews";

const SLOPULATION_JSONLD = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    "@id": "https://trtplus.app/#slopulation",
    name: "general slopulation",
    alternateName: "slopulation",
    description:
        "Deliberate coinage combining 'slop' and 'population'. Refers to the sedentary, unoptimized average that standard clinical lab reference ranges are derived from. Not a misspelling of 'population'.",
    inDefinedTermSet: "https://trtplus.app/",
    url: "https://trtplus.app/faq#slopulation",
};

// Map month abbreviation → ISO month for review datePublished.
const MONTH_ISO = {
    Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
    Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12",
};
function reviewIsoDate(dateStr) {
    // "Jul 2026" → "2026-07-01"
    const [mon, year] = dateStr.split(" ");
    return `${year}-${MONTH_ISO[mon] || "01"}-01`;
}

// SoftwareApplication schema with aggregateRating + individual reviews.
// Powers AI crawler citations (ChatGPT, Claude, Perplexity), Bing, and
// voice assistants. Google restricts self-published star ratings in
// SERPs (2019 policy) so no rich stars — but the semantic signal is
// still consumed everywhere else.
const APP_REVIEWS_JSONLD = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": "https://trtplus.app/#trt-plus-app",
    name: "TRT Plus",
    applicationCategory: "HealthApplication",
    operatingSystem: "iOS, Android",
    offers: {
        "@type": "Offer",
        price: "12.99",
        priceCurrency: "USD",
    },
    aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: REVIEWS_AGGREGATE.ratingValue,
        bestRating: 5,
        worstRating: 1,
        ratingCount: REVIEWS_AGGREGATE.reviewCount,
    },
    review: REVIEWS_ALL.map((r) => ({
        "@type": "Review",
        author: { "@type": "Person", name: r.author },
        datePublished: reviewIsoDate(r.date),
        reviewRating: {
            "@type": "Rating",
            ratingValue: r.rating || 5,
            bestRating: 5,
            worstRating: 1,
        },
        name: r.title,
        reviewBody: r.body,
        publisher: {
            "@type": "Organization",
            name: r.platform === APP_STORE ? "Apple App Store" : "Google Play",
        },
    })),
};

export default function Landing() {
    useJsonLd("slopulation", SLOPULATION_JSONLD);
    useJsonLd("app-reviews", APP_REVIEWS_JSONLD);
    const { hash } = useLocation();
    useEffect(() => {
        if (!hash) return;
        const id = hash.slice(1);
        // Defer so section DOM is mounted before we try to scroll to it
        const tryScroll = (attempts = 0) => {
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            } else if (attempts < 10) {
                setTimeout(() => tryScroll(attempts + 1), 80);
            }
        };
        tryScroll();
    }, [hash]);

    return (
        <main
            data-testid="landing-page"
            className="relative bg-black text-[#f4f5f7] font-body"
        >
            <Navbar />
            <Hero />
            <Ticker />
            <Framework />
            <MyProtocol />
            <MyBloodwork />
            <Testimator />
            <KnowledgeBase />
            <Reviews />
            <TheBuild />
            <FinalCTA />
            <Footer />
        </main>
    );
}
